import { useEffect, useRef, useState } from "react";
import HomeService from "../../services/HomeService";
import { getContentLanguage } from "../../services/LangPrefs";
import Logger from "../../services/Logger";
import { getHomeStoryRowId, getLocalizedStoryFromHomeStoryRo } from "./storyRoUtils";

const FULL_TEXT_MIN_CHARS = 80;

/**
 * Load story-of-the-day or a story by id, then fetch full detail when list payload text is short.
 * @param {string | undefined} featuredStoryId
 */
export function useHomeStoryDetail(featuredStoryId) {
    const contentLanguage = getContentLanguage();
    const [row, setRow] = useState(null);
    const [loading, setLoading] = useState(true);
    const [detailLoading, setDetailLoading] = useState(false);
    const [error, setError] = useState(null);
    const detailFetchedForIdRef = useRef(null);

    useEffect(() => {
        detailFetchedForIdRef.current = null;
    }, [featuredStoryId]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            setRow(null);
            try {
                const sid =
                    typeof featuredStoryId === "string" && featuredStoryId.trim() !== ""
                        ? featuredStoryId.trim()
                        : "";
                const data = sid
                    ? await HomeService.getStoryById(sid)
                    : await HomeService.getStoryOfTheDay(contentLanguage);
                if (cancelled) return;
                if (sid) {
                    if (data && typeof data === "object") {
                        setRow(data);
                        setError(null);
                    } else {
                        setRow(null);
                        setError("not-found");
                    }
                } else {
                    setRow(data && typeof data === "object" ? data : null);
                    setError(data ? null : "empty");
                }
            } catch (e) {
                Logger.error("useHomeStoryDetail:", e);
                if (!cancelled) {
                    setError(e?.message || "error");
                    setRow(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [featuredStoryId, contentLanguage]);

    useEffect(() => {
        if (loading || !row) return undefined;
        const { text } = getLocalizedStoryFromHomeStoryRo(row, contentLanguage);
        if (String(text || "").trim().length > FULL_TEXT_MIN_CHARS) return undefined;

        const storyId = getHomeStoryRowId(row);
        if (!storyId || detailFetchedForIdRef.current === storyId) return undefined;

        detailFetchedForIdRef.current = storyId;
        let cancelled = false;
        setDetailLoading(true);
        HomeService.getStoryById(storyId)
            .then((detail) => {
                if (!cancelled && detail && detail?.code !== "NO_DATA") {
                    setRow(detail);
                }
            })
            .catch((e) => {
                Logger.error("useHomeStoryDetail detail:", e);
            })
            .finally(() => {
                if (!cancelled) setDetailLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [loading, row, contentLanguage]);

    return { row, loading, detailLoading, error, contentLanguage };
}
